import { isNull, isUndefined } from 'lodash/fp';
import React, { useCallback, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { useFetchStarted } from '../../../../hooks/useFetchStarted';
import { useWindowResize } from '../../../../hooks/useWindowResize';
import { Button } from '../../../shared/button/Button';
import { Scrollable } from '../../../shared/scrollable/Scrollable';
import { BreakdownColumnSort, fetchCustomQueriesAnalyticsStarted, getCustomDocumentQueryAnalytics, getIsFetchingCustomQueryAnalytics, SelectedClientOrUser } from './store';
import styles from './ChatBot.module.scss';
import { ChatBotBreakdown, initialBreakdownColumnSort, initialUserBreakdownColumnSort } from './ChatBotBreakdown';
import { ChatBotPieCharts } from './ChatBotPieCharts';

export const NAV_BAR = 100;
export const HEADER = 50;
export const PADDING = 20;
export const MARGINS = 50;
export const HEIGHT_OFFSET = NAV_BAR + HEADER + PADDING + MARGINS;
export const WIDTH_OFFSET = 80;

export const ChatBotAnalytics: React.FC = () => {
    const dispatch = useAppDispatch();

    useFetchStarted([fetchCustomQueriesAnalyticsStarted()]);
    const customQueryAnalytics = useAppSelector(getCustomDocumentQueryAnalytics);
    const isFetchingAnalytics = useAppSelector(getIsFetchingCustomQueryAnalytics);
    const [screenWidth, screenHeight] = useWindowResize();
    const wrapperWidth = screenWidth - WIDTH_OFFSET;
    const wrapperHeight = ((screenHeight - HEIGHT_OFFSET) / 2);
    const [selectedClient, setSelectedClient] = useState<SelectedClientOrUser | null>(null);
    const [selectedUser, setSelectedUser] = useState<SelectedClientOrUser | null>(null);
    const [breakdownColumnOrder, setBreakdownColumnOrder] = useState<BreakdownColumnSort>(initialBreakdownColumnSort);
    const [userBreakdownColumnOrder, setUserBreakdownColumnOrder] = useState<BreakdownColumnSort>(initialUserBreakdownColumnSort);

    const { questionsAskedPerClient, questionsAskedPerClientUser } = customQueryAnalytics;

    const chartDimensions = useMemo(() => ({ height: wrapperHeight, width: wrapperWidth / 3 }), [wrapperHeight, wrapperWidth]);

    const clientOrUserKeys = useMemo(() => {
        let keys: string[] = [];
        if (isNull(selectedClient) && !isUndefined(questionsAskedPerClient)) {
            keys = Object.keys(questionsAskedPerClient!);
        }
        if (!isNull(selectedClient) && !isUndefined(questionsAskedPerClientUser)) {
            keys = Object.keys(questionsAskedPerClientUser!);
        }
        return keys;
    }, [selectedClient, questionsAskedPerClient, questionsAskedPerClientUser]);

    const goBack = useCallback(() => {
        setBreakdownColumnOrder(initialBreakdownColumnSort);
        setUserBreakdownColumnOrder(initialUserBreakdownColumnSort);
        if (selectedUser) {
            setSelectedUser(null);
            dispatch(fetchCustomQueriesAnalyticsStarted(selectedClient!.id));
            return;
        }
        if (selectedClient) {
            setSelectedClient(null);
            dispatch(fetchCustomQueriesAnalyticsStarted());
        }
    }, [selectedUser, selectedClient, dispatch]);

    const setSelectedClientOrUser = useCallback((id: number, name: string) => {
        setBreakdownColumnOrder(initialBreakdownColumnSort);
        setUserBreakdownColumnOrder(initialUserBreakdownColumnSort);
        if (isUndefined(questionsAskedPerClientUser)) {
            setSelectedClient({ id, name });
            dispatch(fetchCustomQueriesAnalyticsStarted(id));
        } else {
            setSelectedUser({ id, name });
            dispatch(fetchCustomQueriesAnalyticsStarted(selectedClient!.id, id));
        }
    }, [questionsAskedPerClientUser, selectedClient, dispatch, setBreakdownColumnOrder, setUserBreakdownColumnOrder, setSelectedClient, setSelectedUser]);

    const showBackButton = useMemo(() => selectedUser || selectedClient, [selectedUser, selectedClient]);

    return (
        <div className={styles.aiChatBotWrapper} data-testid='chat-bot-wrapper'>
            <div className={styles.aiChatBotTitleWrapper}>
                <div className={styles.aiChatBotTitle} data-testid='chat-bot-title'>Chat Bot Analytics</div>
            </div>
            <div className={styles.aiChatBotContentWrapper}>
                <div className={styles.chartsWrapper} style={{ height: wrapperHeight, width: wrapperWidth }}>
                    <ChatBotPieCharts
                        customQueryAnalytics={customQueryAnalytics}
                        isFetchingAnalytics={isFetchingAnalytics}
                        clientOrUserKeys={clientOrUserKeys}
                        chartDimensions={chartDimensions}
                        selectedClient={selectedClient}
                        selectedUser={selectedUser}
                        setSelectedClientOrUser={setSelectedClientOrUser}
                    />
                </div>
                <div className={styles.listWrapper} style={{ height: wrapperHeight - (showBackButton ? 50 : 0), width: wrapperWidth }}>
                    <Scrollable>
                        <ChatBotBreakdown
                            customQueryAnalytics={customQueryAnalytics}
                            setBreakdownColumnOrder={setBreakdownColumnOrder}
                            setUserBreakdownColumnOrder={setUserBreakdownColumnOrder}
                            breakdownColumnOrder={breakdownColumnOrder}
                            userBreakdownColumnOrder={userBreakdownColumnOrder}
                            clientOrUserKeys={clientOrUserKeys}
                            selectedClient={selectedClient}
                            selectedUser={selectedUser}
                            setSelectedClientOrUser={setSelectedClientOrUser}
                        />
                    </Scrollable>
                </div>
                <div className={styles.buttonWrapper} style={{ width: wrapperWidth }}>
                    {showBackButton && <Button label='Back' onClick={goBack} />}
                </div>
            </div>
        </div>
    );
};
